import { createReducer } from '@common/redux-utils';
import {
  CustomerActionType,
  GetCustomerFailureAction,
  GetCustomerStartAction,
  GetCustomerSuccessAction,
  SaveCustomerFailureAction,
  SaveCustomerStartAction,
  SaveCustomerSuccessAction,
  SearchCustomersInitAction,
  SearchCustomersFailureAction,
  SearchCustomersSuccessAction,
  SearchContractsByCustomerInitAction,
  SearchContractsByCustomerFailureAction,
  SearchContractsByCustomerSuccessAction,
  SaveCustomerFilesStartAction,
  SaveCustomerFilesSuccessAction,
  SaveCustomerFilesFailureAction,
  RemoveCustomerFileStartAction,
  RemoveCustomerFileSuccessAction,
  RemoveCustomerFileFailureAction,
  RelateContractToCustomerSuccessAction,
  RelateContractToCustomerFailureAction,
  UnrelateContractToCustomerSuccessAction,
  UnrelateContractToCustomerFailureAction,
  SearchRelatedContractsByCustomerInitAction,
  SearchRelatedContractsByCustomerFailureAction,
  SearchRelatedContractsByCustomerSuccessAction,
  SetCustomerAddressesAction
} from './customer.actions';
import { CustomerViewModel } from '@models/Customer';
import { blankOptionsVM, OptionsViewModel } from '@models/common.models';
import { ContractSimpleViewModel } from '@models/Contract';
import { LoadCustomerIntoDetailsAction } from './customer.actions';
import { ClearCustomerAction } from './_components/customer-picker/customer-picker.actions';

// Defining the state of the page.
// Should be one per page.
// All properties should be readonly.
export interface CustomerState {
  readonly message?: string;
  readonly customerVM: CustomerViewModel;
  readonly searchQuery: string | null;
  readonly searchLoading: boolean;
  readonly searchPageCount: number;
  readonly searchPageNumber: number;
  readonly searchPageSize: number;
  readonly searchResults: CustomerViewModel[];
  readonly searchResultsTotalCount: number;
  readonly contractsLoading: boolean;
  readonly contractsPageCount: number;
  readonly contractsPageNumber: number;
  readonly contractsPageSize: number;
  readonly contractsResults: ContractSimpleViewModel[];
  readonly relatedContractsLoading: boolean;
  readonly relatedContractsPageCount: number;
  readonly relatedContractsPageNumber: number;
  readonly relatedContractsPageSize: number;
  readonly relatedContractsResults: ContractSimpleViewModel[];
  readonly customerPhoneTypes: OptionsViewModel[];
  readonly saving?: boolean;
}

var c = {
  id: 0,
  name: '',
  customerType: blankOptionsVM,
  customerStatus: blankOptionsVM,
  language: blankOptionsVM,
  emailAddress: '',
  phone: '',
  phone1Type: blankOptionsVM,
  usps: false,
  email: false
} as CustomerViewModel;

// This is the initial state of the page
const initialState: CustomerState = {
  message: 'Initialized',
  customerVM: c,
  searchQuery: null,
  searchLoading: false,
  searchPageCount: 0,
  searchPageNumber: 1,
  searchPageSize: 10,
  searchResults: [],
  searchResultsTotalCount: 0,
  contractsLoading: false,
  contractsPageCount: 0,
  contractsPageNumber: 1,
  contractsPageSize: 10,
  contractsResults: [],
  relatedContractsLoading: false,
  relatedContractsPageCount: 0,
  relatedContractsPageNumber: 1,
  relatedContractsPageSize: 10,
  relatedContractsResults: [],
  customerPhoneTypes: []
};

/********** Save **********/
function handleSaveCustomerStart(state: CustomerState, action: SaveCustomerStartAction): CustomerState {
  return {
    ...state,
    saving: true
  };
}
function handleSaveCustomerSuccess(state: CustomerState, action: SaveCustomerSuccessAction): CustomerState {
  if (!action.currentId) {
    return state;
  }
  return {
    ...state,
    saving: false,
    message: 'SUCCESS - customer saved - ' + action.currentId
  };
}
function handleSaveCustomerFailure(state: CustomerState, action: SaveCustomerFailureAction): CustomerState {
  if (!action.errorMessage) {
    return {
      ...state,
      saving: false
    };
  }
  return {
    ...state,
    saving: false,
    message: 'ERROR - save customer failed - ' + action.errorMessage
  };
}

/********** Get **********/
function handleGetCustomerStart(state: CustomerState, action: GetCustomerStartAction): CustomerState {
  return state;
}
function handleGetCustomerSuccess(state: CustomerState, action: GetCustomerSuccessAction): CustomerState {
  if (!action.customer) {
    return state;
  }

  return {
    ...state,
    message: 'SUCCESS - get customer done - ' + action.customer.id,
    customerVM: action.customer
  };
}
function handleGetCustomerFailure(state: CustomerState, action: GetCustomerFailureAction): CustomerState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: 'ERROR - get customer failed - ' + action.errorMessage
  };
}

/********** Clear **********/
function handleClearCustomer(state: CustomerState, action: ClearCustomerAction): CustomerState {
  return {
    ...state,
    customerVM: c
  };
}

/********** SetCustomerAddresses **********/
function handleSetCustomerAddresses(state: CustomerState, action: SetCustomerAddressesAction): CustomerState {
  const updatedCustomer = { ...state.customerVM, addresses: action.addresses };
  return {
    ...state,
    customerVM: updatedCustomer
  };
}

/********** Search **********/
function handleSearchCustomersInit(state: CustomerState, action: SearchCustomersInitAction): CustomerState {
  return {
    ...state,
    message: 'Search Init!',
    searchQuery: action.query,
    searchLoading: true
  };
}
function handleSearchCustomersFailure(state: CustomerState, action: SearchCustomersFailureAction): CustomerState {
  return {
    ...state,
    message: action.errorMessage,
    searchLoading: false
  };
}
function handleSearchCustomersSuccess(state: CustomerState, action: SearchCustomersSuccessAction): CustomerState {
  return {
    ...state,
    message: 'Search Success!',
    searchLoading: false,
    searchPageCount: Math.ceil(action.searchResult.totalResults / action.searchResult.pageSize),
    searchPageNumber: action.pageNumber,
    searchPageSize: action.pageSize,
    searchResults: action.searchResult.results,
    searchResultsTotalCount: action.searchResult.totalResults
  };
}

/********** SearchContractsByCustomer **********/
function handleSearchContractsByCustomerInit(
  state: CustomerState,
  action: SearchContractsByCustomerInitAction
): CustomerState {
  return {
    ...state,
    message: 'Search Init!',
    // searchQuery: action.query,
    contractsLoading: true
  };
}
function handleSearchContractsByCustomerFailure(
  state: CustomerState,
  action: SearchContractsByCustomerFailureAction
): CustomerState {
  return {
    ...state,
    message: action.errorMessage,
    contractsLoading: false
  };
}
function handleSearchContractsByCustomerSuccess(
  state: CustomerState,
  action: SearchContractsByCustomerSuccessAction
): CustomerState {
  return {
    ...state,
    message: 'Search Success!',
    contractsLoading: false,
    contractsPageCount: Math.ceil(action.searchResult.totalResults / action.searchResult.pageSize),
    contractsPageNumber: action.pageNumber,
    contractsPageSize: action.pageSize,
    contractsResults: action.searchResult.results
  };
}

/********** SearchRelatedContractsByCustomer **********/
function handleSearchRelatedContractsByCustomerInit(
  state: CustomerState,
  action: SearchRelatedContractsByCustomerInitAction
): CustomerState {
  return {
    ...state,
    message: 'Search Init!',
    // searchQuery: action.query,
    relatedContractsLoading: true
  };
}
function handleSearchRelatedContractsByCustomerFailure(
  state: CustomerState,
  action: SearchRelatedContractsByCustomerFailureAction
): CustomerState {
  return {
    ...state,
    message: action.errorMessage,
    relatedContractsLoading: false
  };
}
function handleSearchRelatedContractsByCustomerSuccess(
  state: CustomerState,
  action: SearchRelatedContractsByCustomerSuccessAction
): CustomerState {
  return {
    ...state,
    message: 'Search Success!',
    relatedContractsLoading: false,
    relatedContractsPageCount: Math.ceil(action.searchResult.totalResults / action.searchResult.pageSize),
    relatedContractsPageNumber: action.pageNumber,
    relatedContractsPageSize: action.pageSize,
    relatedContractsResults: action.searchResult.results
  };
}

/********** SaveCustomerFiles **********/
function handleSaveCustomerFilesStart(state: CustomerState, action: SaveCustomerFilesStartAction): CustomerState {
  return state;
}
function handleSaveCustomerFilesSuccess(state: CustomerState, action: SaveCustomerFilesSuccessAction): CustomerState {
  if (!action.documents) {
    return state;
  }
  return {
    ...state,
    message: 'SUCCESS - customer files saved - ' + action.customerId
  };
}
function handleSaveCustomerFilesFailure(state: CustomerState, action: SaveCustomerFilesFailureAction): CustomerState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: 'ERROR - save customer files failed - ' + action.errorMessage
  };
}

/********** RemoveCustomerFile **********/
function handleRemoveCustomerFileStart(state: CustomerState, action: RemoveCustomerFileStartAction): CustomerState {
  return state;
}
function handleRemoveCustomerFileSuccess(state: CustomerState, action: RemoveCustomerFileSuccessAction): CustomerState {
  if (!action.fileId) {
    return state;
  }
  return {
    ...state,
    message: 'SUCCESS - customer file removed - ' + action.customerId
  };
}
function handleRemoveCustomerFileFailure(state: CustomerState, action: RemoveCustomerFileFailureAction): CustomerState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: 'ERROR - remove customer file failed - ' + action.errorMessage
  };
}

function handleLoadCustomerIntoDetails(state: CustomerState, action: LoadCustomerIntoDetailsAction): CustomerState {
  return {
    ...state,
    customerVM: action.customer
  };
}

/********** RelateContractToCustomer **********/
function handleRelateContractToCustomerSuccess(
  state: CustomerState,
  action: RelateContractToCustomerSuccessAction
): CustomerState {
  return {
    ...state,
    message: 'SUCCESS - contract associated to customer - ' + action.customerContract.customerId
  };
}

function handleRelateContractToCustomerFailure(
  state: CustomerState,
  action: RelateContractToCustomerFailureAction
): CustomerState {
  return {
    ...state,
    message: 'ERROR - associating contract failed - ' + action.errorMessage
  };
}

/********** UnrelateContractToCustomer **********/
function handleUnrelateContractToCustomerSuccess(
  state: CustomerState,
  action: UnrelateContractToCustomerSuccessAction
): CustomerState {
  return {
    ...state,
    message: 'SUCCESS - contract unassociated to customer - ' + action.customerContract.customerId
  };
}

function handleUnrelateContractToCustomerFailure(
  state: CustomerState,
  action: UnrelateContractToCustomerFailureAction
): CustomerState {
  return {
    ...state,
    message: 'ERROR - unassociating contract failed - ' + action.errorMessage
  };
}

// This creates the reducer as redux wants it w/ a helper function
// There should an entry for each action type
export const CustomerReducer = createReducer(initialState, {
  /********** Save **********/
  [CustomerActionType.SaveCustomerStart]: handleSaveCustomerStart,
  [CustomerActionType.SaveCustomerSuccess]: handleSaveCustomerSuccess,
  [CustomerActionType.SaveCustomerFailure]: handleSaveCustomerFailure,

  /********** Get **********/
  [CustomerActionType.GetCustomerStart]: handleGetCustomerStart,
  [CustomerActionType.GetCustomerSuccess]: handleGetCustomerSuccess,
  [CustomerActionType.GetCustomerFailure]: handleGetCustomerFailure,

  /********** Clear **********/
  [CustomerActionType.ClearCustomer]: handleClearCustomer,

  /********** SetCustomerAddresses **********/
  [CustomerActionType.SetCustomerAddresses]: handleSetCustomerAddresses,

  /********** Search **********/
  [CustomerActionType.SearchCustomersInit]: handleSearchCustomersInit,
  [CustomerActionType.SearchCustomersFailure]: handleSearchCustomersFailure,
  [CustomerActionType.SearchCustomersSuccess]: handleSearchCustomersSuccess,

  /********** SearchContractsByCustomer **********/
  [CustomerActionType.SearchContractsByCustomerInit]: handleSearchContractsByCustomerInit,
  [CustomerActionType.SearchContractsByCustomerFailure]: handleSearchContractsByCustomerFailure,
  [CustomerActionType.SearchContractsByCustomerSuccess]: handleSearchContractsByCustomerSuccess,

  /********** SearchRelatedContractsByCustomer **********/
  [CustomerActionType.SearchRelatedContractsByCustomerInit]: handleSearchRelatedContractsByCustomerInit,
  [CustomerActionType.SearchRelatedContractsByCustomerFailure]: handleSearchRelatedContractsByCustomerFailure,
  [CustomerActionType.SearchRelatedContractsByCustomerSuccess]: handleSearchRelatedContractsByCustomerSuccess,

  /********** SaveCustomerFiles **********/
  [CustomerActionType.SaveCustomerFilesStart]: handleSaveCustomerFilesStart,
  [CustomerActionType.SaveCustomerFilesSuccess]: handleSaveCustomerFilesSuccess,
  [CustomerActionType.SaveCustomerFilesFailure]: handleSaveCustomerFilesFailure,

  /********** RemoveCustomerFile **********/
  [CustomerActionType.RemoveCustomerFileStart]: handleRemoveCustomerFileStart,
  [CustomerActionType.RemoveCustomerFileSuccess]: handleRemoveCustomerFileSuccess,
  [CustomerActionType.RemoveCustomerFileFailure]: handleRemoveCustomerFileFailure,

  [CustomerActionType.LoadCustomerIntoDetails]: handleLoadCustomerIntoDetails,

  /********** RelateContractToCustomer **********/
  [CustomerActionType.RelateContractToCustomerSuccess]: handleRelateContractToCustomerSuccess,
  [CustomerActionType.RelateContractToCustomerFailure]: handleRelateContractToCustomerFailure,

  /********** UnrelateContractToCustomer **********/
  [CustomerActionType.UnrelateContractToCustomerSuccess]: handleUnrelateContractToCustomerSuccess,
  [CustomerActionType.UnrelateContractToCustomerFailure]: handleUnrelateContractToCustomerFailure
});



// WEBPACK FOOTER //
// ./src/customer/customer.reducer.ts